/**
 * SmartLoginForm Component
 * 
 * Formulario de login simplificado con detección automática de tipo.
 * Incluye botón para cambiar a modo teléfono internacional con PhoneInput.
 * 
 * @module login/components/SmartLoginForm
 */

import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Button, Form, Input, Space, Tooltip } from 'antd';
import { WhatsAppOutlined, MailOutlined, PhoneOutlined, UserOutlined, GlobalOutlined } from '@ant-design/icons';
import { detectInputType, DetectionResult, DetectedInputType, hasLetter, validatePhone } from '../hooks/useInputTypeDetection';
import { ErrorMessage } from './loginUtils';
import { useAppDispatch, useAppSelector, RootState } from '@store/store';
import { login } from '@store/slices/authSlice';
import PhoneInput from '@tools/components/PhoneInput';
import { E164Number } from 'libphonenumber-js';
import { useThemeMode } from '@theme/hooks/useThemeMode.tsx';
import 'react-phone-number-input/style.css';

// ============================================================================
// Types
// ============================================================================

interface SmartLoginFormProps {
    /** Callback cuando el login es exitoso */
    onSuccess?: () => void;
    /** Callback para navegar a recuperar contraseña */
    onForgotPassword?: () => void;
    /** Callback para navegar a registro */
    onRegister?: () => void;
    /** Callback para login con WhatsApp */
    onWhatsAppLogin?: () => void;
    /** Radio de borde de botones */
    buttonRadius?: string;
}

// ============================================================================
// Helper: Icono de tipo detectado
// ============================================================================

function TypeIndicator({ type, isPhoneMode }: { type: DetectedInputType; isPhoneMode: boolean }) {
    const iconStyle = { fontSize: 14, marginRight: 4 };

    if (isPhoneMode) {
        return <><PhoneOutlined style={{ ...iconStyle, color: '#52c41a' }} /> Teléfono Internacional</>;
    }

    switch (type) {
        case 'email':
            return <><MailOutlined style={{ ...iconStyle, color: '#1890ff' }} /> Email</>;
        case 'phone':
            return <><PhoneOutlined style={{ ...iconStyle, color: '#52c41a' }} /> Teléfono</>;
        case 'username':
            return <><UserOutlined style={{ ...iconStyle, color: '#722ed1' }} /> Usuario</>;
        default:
            return 'Email, teléfono o usuario';
    }
}

// ============================================================================
// Phone Mode Toggle Button
// ============================================================================

function PhoneModeButton({ isPhoneMode, onClick, visible = true }: { isPhoneMode: boolean; onClick: () => void; visible?: boolean }) {
    // No renderizar si no es visible (cuando no hay patrón numérico)
    if (!visible && !isPhoneMode) {
        return null;
    }

    return (
        <Tooltip title={isPhoneMode ? 'Volver a texto' : 'Teléfono internacional'}>
            <button
                type="button"
                onClick={onClick}
                style={{
                    background: isPhoneMode ? '#52c41a' : 'transparent',
                    border: isPhoneMode ? 'none' : '1px solid #d9d9d9',
                    borderRadius: '4px',
                    padding: '4px 8px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                    color: isPhoneMode ? '#fff' : '#8c8c8c',
                    transition: 'all 0.2s ease',
                }}
                aria-label={isPhoneMode ? 'Volver a modo texto' : 'Cambiar a teléfono internacional'}
                aria-pressed={isPhoneMode}
            >
                <GlobalOutlined style={{ fontSize: 14 }} />
                <PhoneOutlined style={{ fontSize: 14 }} />
            </button>
        </Tooltip>
    );
}

// ============================================================================
// Main Component
// ============================================================================

/**
 * SmartLoginForm - Formulario de login con detección automática
 * 
 * Features:
 * - Detección automática de email/phone/username
 * - Botón para cambiar a PhoneInput internacional
 * - Auto-retorno a texto si se vacía o detecta letras
 */
export function SmartLoginForm({
    onSuccess: _onSuccess,
    onForgotPassword,
    onWhatsAppLogin,
    onRegister,
    buttonRadius = '8px',
}: SmartLoginFormProps) {
    const dispatch = useAppDispatch();
    const authStatus = useAppSelector((state: RootState) => state.auth.status);
    const authError = useAppSelector((state: RootState) => state.auth.error);
    const { isDark } = useThemeMode();

    // Estado del input
    const [inputValue, setInputValue] = useState('');
    const [phoneValue, setPhoneValue] = useState<E164Number | undefined>(undefined);
    const [isPhoneMode, setIsPhoneMode] = useState(false);
    const [detection, setDetection] = useState<DetectionResult>({
        detectedType: null,
        formattedValue: '',
        errorMessage: null,
        isValid: false,
    });
    const [password, setPassword] = useState('');

    const inputRef = useRef<HTMLInputElement>(null);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const phoneInputRef = useRef<any>(null);

    /**
     * Detectar valores autocomplete del navegador al cargar
     * Los navegadores aplican autofill después del mount, revisamos con delay
     */
    useEffect(() => {
        const checkAutofill = () => {
            // Verificar si el input tiene valor (autofill del navegador)
            const currentInput = inputRef.current;
            if (currentInput && currentInput.value && currentInput.value !== inputValue) {
                setInputValue(currentInput.value);
                const result = detectInputType(currentInput.value);
                setDetection(result);
            }
        };

        // Revisar después de un delay (el autofill puede aplicarse después del mount)
        const timer1 = setTimeout(checkAutofill, 100);
        const timer2 = setTimeout(checkAutofill, 500);
        const timer3 = setTimeout(checkAutofill, 1000);

        return () => {
            clearTimeout(timer1);
            clearTimeout(timer2);
            clearTimeout(timer3);
        };
    }, []);

    /**
     * Auto-retorno a modo texto cuando PhoneInput se vacía
     */
    useEffect(() => {
        if (isPhoneMode && !phoneValue) {
            // Si el campo está vacío, volver a modo texto
            // Pequeño delay para no interferir con interacción
            const timer = setTimeout(() => {
                if (!phoneValue) {
                    setIsPhoneMode(false);
                }
            }, 500);
            return () => clearTimeout(timer);
        }
    }, [phoneValue, isPhoneMode]);

    /**
     * Detectar tipo cuando el usuario sale del input texto
     */
    const handleBlur = useCallback(() => {
        if (!isPhoneMode) {
            const result = detectInputType(inputValue);
            setDetection(result);
        }
    }, [inputValue, isPhoneMode]);

    /**
     * Manejar cambio de input texto
     */
    const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        setInputValue(newValue);

        // Limpiar error mientras escribe
        if (detection.errorMessage) {
            setDetection(prev => ({ ...prev, errorMessage: null }));
        }
    }, [detection.errorMessage]);

    /**
     * Manejar cambio de PhoneInput
     */
    const handlePhoneChange = useCallback((value: E164Number | undefined) => {
        setPhoneValue(value);

        // Limpiar error mientras escribe
        if (detection.errorMessage) {
            setDetection(prev => ({ ...prev, errorMessage: null }));
        }

        // Si detecta letras (el usuario pegó texto con letras), volver a modo texto
        if (value && hasLetter(value)) {
            setIsPhoneMode(false);
            setInputValue(value);
            setPhoneValue(undefined);
        }
    }, [detection.errorMessage]);

    /**
     * Validar teléfono en blur (modo PhoneInput)
     */
    const handlePhoneBlur = useCallback(() => {
        if (isPhoneMode && phoneValue) {
            const validation = validatePhone(phoneValue);
            setDetection({
                detectedType: 'phone',
                formattedValue: phoneValue,
                errorMessage: validation.error,
                isValid: validation.isValid,
            });
        }
    }, [isPhoneMode, phoneValue]);

    /**
     * Toggle modo teléfono
     */
    const handleTogglePhoneMode = useCallback(() => {
        // Limpiar estado de validación al cambiar de modo
        setDetection({
            detectedType: null,
            formattedValue: '',
            errorMessage: null,
            isValid: false,
        });

        if (isPhoneMode) {
            // Volver a texto: pasar valor del phone al input texto
            setIsPhoneMode(false);
            if (phoneValue) {
                setInputValue(phoneValue);
            }
            setPhoneValue(undefined);
            // Focus en input texto
            setTimeout(() => inputRef.current?.focus(), 50);
        } else {
            // Cambiar a phone: pasar valor del texto al phone si es numérico
            setIsPhoneMode(true);
            const numericValue = inputValue.replace(/\D/g, '');
            if (numericValue) {
                // Si tiene números, intentar pasarlos
                setPhoneValue((inputValue.startsWith('+') ? inputValue : `+54${numericValue}`) as E164Number);
            }
            setInputValue('');
            // Focus en phone input
            setTimeout(() => phoneInputRef.current?.focus?.(), 50);
        }
    }, [isPhoneMode, inputValue, phoneValue]);

    /**
     * Enviar formulario
     */
    const handleSubmit = useCallback(() => {
        let finalValue = '';
        let loginType: DetectedInputType = 'username';

        if (isPhoneMode) {
            // Modo teléfono: usar phoneValue directamente
            if (!phoneValue) {
                setDetection({ detectedType: 'phone', formattedValue: '', errorMessage: 'Ingresa un número de teléfono', isValid: false });
                return;
            }
            finalValue = phoneValue;
            loginType = 'phone';
        } else {
            // Modo texto: ejecutar detección
            const result = detectInputType(inputValue);
            setDetection(result);

            if (!result.isValid) {
                return;
            }
            finalValue = result.formattedValue;
            loginType = result.detectedType || 'username';
        }

        if (!password) {
            return;
        }

        // Dispatch login
        dispatch(login({
            username: finalValue,
            password,
            login_type: loginType,
        }));
    }, [isPhoneMode, inputValue, phoneValue, password, dispatch]);

    const isLoading = authStatus === 'loading';

    const handleGoogleLogin = useCallback(() => {
        const authOrigin = (import.meta.env.VITE_AUTH_ORIGIN || 'https://auth.ahoravirtual.com.ar').replace(/\/$/, '');
        const returnPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
        const query = new URLSearchParams({
            tenant_host: window.location.host,
            return_path: returnPath || '/',
        });
        window.location.assign(`${authOrigin}/sso/start?${query.toString()}`);
    }, []);

    // Valor actual para mostrar en label
    const currentValue = isPhoneMode ? phoneValue : inputValue;

    // Mostrar botón de teléfono solo si el input parece numérico (empieza con + o dígito)
    const showPhoneButton = isPhoneMode || /^[+\d]/.test(inputValue.trim());

    return (
        <div className="smart-login-form">
            <Form
                layout="vertical"
                onFinish={handleSubmit}
                disabled={isLoading}
                aria-busy={isLoading}
                className="mb-5"
            >
                {/* Input unificado con detección automática */}
                <Form.Item
                    label={
                        <span style={{ fontSize: 16, fontWeight: 500 }}>
                            {currentValue ? (
                                <TypeIndicator type={detection.detectedType} isPhoneMode={isPhoneMode} />
                            ) : (
                                'Email, teléfono o usuario'
                            )}
                        </span>
                    }
                    validateStatus={detection.errorMessage ? 'error' : undefined}
                    help={detection.errorMessage}
                    className="mb-3"
                >
                    {isPhoneMode ? (
                        // Modo teléfono internacional
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <div style={{ flex: 1 }}>
                                <PhoneInput
                                    placeholder="Número de teléfono"
                                    value={phoneValue}
                                    onChange={handlePhoneChange}
                                    onBlur={handlePhoneBlur}
                                    defaultCountry="AR"
                                    international
                                    ref={phoneInputRef}
                                    style={{ width: '100%' }}
                                />
                            </div>
                            <PhoneModeButton isPhoneMode={isPhoneMode} onClick={handleTogglePhoneMode} />
                        </div>
                    ) : (
                        // Modo texto con detección automática
                        <Input
                            ref={inputRef as any}
                            value={inputValue}
                            onChange={handleInputChange}
                            onBlur={handleBlur}
                            placeholder="Ingresa tu email, teléfono o usuario"
                            size="large"
                            autoComplete="username email tel"
                            aria-invalid={!!detection.errorMessage}
                            suffix={<PhoneModeButton isPhoneMode={isPhoneMode} onClick={handleTogglePhoneMode} visible={showPhoneButton} />}
                        />
                    )}
                </Form.Item>

                {/* Mostrar error global si existe */}
                <ErrorMessage error={authError} />

                {/* Campo de contraseña */}
                <Form.Item
                    label={<span style={{ fontSize: 16, fontWeight: 500 }}>Contraseña</span>}
                    className="mb-3"
                >
                    <Input.Password
                        value={password}
                        onChange={e => setPassword(e.target.value)}
                        placeholder="Contraseña"
                        autoComplete="current-password"
                        size="large"
                    />
                </Form.Item>

                {/* Botón de submit */}
                <Form.Item className="mb-3">
                    <Button
                        className="bg_web_color_oscuro_3 text_web_color_claro_default hover_web_color_claro_1 hover_border_black"
                        htmlType="submit"
                        block
                        size="large"
                        style={{ height: 48, fontSize: 16, fontWeight: 500, borderRadius: buttonRadius }}
                        loading={isLoading}
                        disabled={isPhoneMode ? !phoneValue || !password : !inputValue || !password}
                    >
                        🔐 Iniciar sesión
                    </Button>
                </Form.Item>

                {/* Link olvidé contraseña */}
                {onForgotPassword && (
                    <div className="w-100 d-flex justify-content-center my-3">
                        <a
                            onClick={onForgotPassword}
                            style={{ color: '#1890ff', fontSize: 14, textDecoration: 'none', cursor: 'pointer' }}
                            onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
                            onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
                        >
                            🔑 ¿Olvidaste tu contraseña?
                        </a>
                    </div>
                )}
            </Form>

            {/* Separador */}
            <div className="w-100 d-flex justify-content-center align-items-center my-4">
                <div style={{ flex: 1, height: 1, background: '#d9d9d9' }} />
                <span style={{ margin: '0 16px', color: '#8c8c8c', fontSize: 14 }}>o continúa con</span>
                <div style={{ flex: 1, height: 1, background: '#d9d9d9' }} />
            </div>

            {/* Botones alternativos */}
            <Space orientation="vertical" className="w-100" size="middle">
                <button
                    type="button"
                    onClick={handleGoogleLogin}
                    aria-label="Iniciar con Google"
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 12,
                        width: '100%',
                        height: 48,
                        padding: '0 16px',
                        borderRadius: buttonRadius,
                        border: isDark ? '1px solid #8e918f' : '1px solid #dadce0',
                        background: isDark ? '#131314' : '#ffffff',
                        color: isDark ? '#e3e3e3' : '#1f1f1f',
                        fontFamily: '"Roboto", "Google Sans", Arial, sans-serif',
                        fontSize: 15,
                        fontWeight: 500,
                        letterSpacing: '0.25px',
                        cursor: 'pointer',
                        transition: 'background 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease',
                    }}
                    onMouseEnter={e => {
                        const btn = e.currentTarget;
                        if (isDark) {
                            btn.style.background = '#2b2b2d';
                            btn.style.borderColor = '#a8aba8';
                        } else {
                            btn.style.background = '#f7f8f8';
                            btn.style.boxShadow = '0 1px 3px 1px rgba(0,0,0,0.15)';
                        }
                    }}
                    onMouseLeave={e => {
                        const btn = e.currentTarget;
                        btn.style.background = isDark ? '#131314' : '#ffffff';
                        btn.style.borderColor = isDark ? '#8e918f' : '#dadce0';
                        btn.style.boxShadow = 'none';
                    }}
                    onMouseDown={e => {
                        e.currentTarget.style.background = isDark ? '#37383a' : '#f0f1f1';
                    }}
                    onMouseUp={e => {
                        e.currentTarget.style.background = isDark ? '#2b2b2d' : '#f7f8f8';
                    }}
                >
                    <img
                        src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
                        alt="Google logo"
                        width={20}
                        height={20}
                        style={{ flexShrink: 0 }}
                    />
                    <span>Iniciar con Google</span>
                </button>
                {onWhatsAppLogin && (
                    <Button
                        block
                        onClick={onWhatsAppLogin}
                        icon={<WhatsAppOutlined />}
                        size="large"
                        style={{ fontWeight: 500, height: 48, borderRadius: buttonRadius, color: 'black' }}
                        className="bg_color_whatsapp hover_web_color_claro_1 hover_border_black"
                    >
                        Iniciar sesión con WhatsApp
                    </Button>
                )}
            </Space>

            {/* Link registro */}
            {onRegister && (
                <div className="w-100 d-flex justify-content-center my-3">
                    <span style={{ fontSize: 14, color: '#8c8c8c' }}>
                        ¿No tienes una cuenta?{' '}
                        <a
                            onClick={onRegister}
                            style={{ color: '#1890ff', textDecoration: 'none', fontWeight: 500, cursor: 'pointer' }}
                            onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
                            onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
                        >
                            Regístrate aquí
                        </a>
                    </span>
                </div>
            )}
        </div>
    );
}

export default SmartLoginForm;
